// The Director characters on the web: the six film lights (a mirror of core/src/directors.ts; directors.test.ts
// checks the two agree), the user's pick as GET /api/me shows it, and a tiny signal for the light's "working"
// flicker (a tool call or a job starting), so the light reacts without any React re-render.
import type { User } from "./types";

export const PRESET_IDS = ["keylight", "lantern", "tube", "daylight", "practical", "spot"] as const;
export type PresetId = (typeof PRESET_IDS)[number];

export type Preset = { id: PresetId; name: string; light: string; tagline: string; voiceId: string; voiceName: string };

export const PRESETS: Preset[] = [
  { id: "keylight", name: "Key Light", light: "Tungsten Fresnel", tagline: "Warm and plain-spoken. Runs the shoot and asks before it spends.", voiceId: "cjVigY5qzO86Huf0OWal", voiceName: "Eric" },
  { id: "lantern", name: "Lantern", light: "China-ball lantern", tagline: "A warm British storyteller. Lovely for bedtime stories and slow reveals.", voiceId: "JBFqnCBsd6RMkjVDRZzb", voiceName: "George" },
  { id: "tube", name: "Tube", light: "LED tube", tagline: "Bright and upbeat. Quick ideas for Shorts, ads and hooks.", voiceId: "EXAVITQu4vr4xnSDxMaL", voiceName: "Sarah" },
  { id: "daylight", name: "Daylight", light: "HMI daylight panel", tagline: "Calm and even. A documentary voice that never hurries.", voiceId: "SAz9YHcvj6GT2YYXdXww", voiceName: "River" },
  { id: "practical", name: "Practical", light: "Desk-lamp bulb", tagline: "Friendly and knowledgeable, like the colleague at the next desk.", voiceId: "XrExE9yKIg1WjnnlVkGX", voiceName: "Matilda" },
  { id: "spot", name: "Spot", light: "Follow-spot", tagline: "A showman with easy confidence. Trailers, launches, big moments.", voiceId: "CwhRBWXzGAHq8TQ4Fs17", voiceName: "Roger" },
];

export const isPresetId = (x: unknown): x is PresetId => typeof x === "string" && (PRESET_IDS as readonly string[]).includes(x);
export const presetOf = (id: unknown): Preset => PRESETS.find((p) => p.id === id) ?? PRESETS[0]!;

/** The user's Director (api persona.ts personaView). */
export type Persona = {
  preset: PresetId;
  name: string;
  customName: boolean;
  voiceId: string;
  voiceName: string | null;
  /** a voice other than the character's own, else null */
  customVoice: string | null;
  presetVoiceId: string;
  /** the user has picked a character (false = Key Light by default) */
  chosen: boolean;
};

export const MAX_NAME = 24;

/** Parses the api's persona; an older api (no `director` on /api/me) gets Key Light with the saved v2 voice. */
export function normPersona(v: unknown, user?: Pick<User, "directorVoiceId" | "directorVoiceName">): Persona {
  const o = v && typeof v === "object" ? (v as Record<string, unknown>) : {};
  const p = presetOf(o.preset);
  const s = (x: unknown) => (typeof x === "string" && x ? x : null);
  const voiceId = s(o.voiceId) ?? user?.directorVoiceId ?? p.voiceId;
  const presetVoiceId = s(o.presetVoiceId) ?? p.voiceId;
  return {
    preset: p.id,
    name: s(o.name) ?? p.name,
    customName: o.customName === true,
    voiceId,
    voiceName: s(o.voiceName) ?? (voiceId === p.voiceId ? p.voiceName : (user?.directorVoiceName ?? null)),
    customVoice: o.customVoice === undefined ? (voiceId !== presetVoiceId ? voiceId : null) : s(o.customVoice),
    presetVoiceId,
    chosen: o.chosen === true,
  };
}

/** What a picker change sends to PUT /api/director/persona. */
export type PersonaChange = { preset?: PresetId; name?: string | null; voiceId?: string | null };

/** The same cleaning the api does (core cleanDirectorName), so the field shows what will be saved. */
export function cleanName(raw: string): string | null {
  const s = raw
    .normalize("NFC")
    .replace(/[^\p{L}\p{N}\p{M} '’.\-&]/gu, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, MAX_NAME)
    .trim();
  return /[\p{L}\p{N}]/u.test(s) ? s : null;
}

// ------------------------------------------------------------------ the "working" flicker signal

type Listener = () => void;
const listeners = new Set<Listener>();
let lastFlicker = 0;

/** The Director started making something (a tool call, a job): every light on screen gives a quick warm flicker. */
export function flickerDirector(now = Date.now()) {
  // one flicker per burst: a tool that starts three jobs flickers once
  if (now - lastFlicker < 900) return;
  lastFlicker = now;
  listeners.forEach((f) => f());
}
export function onDirectorWork(f: Listener): () => void {
  listeners.add(f);
  return () => void listeners.delete(f);
}
/** For tests. */
export function resetFlicker() {
  lastFlicker = 0;
}
